// src/components/wellfinder/WellFinderPanel.jsx
import { useEffect, useState, useCallback, useRef } from "react";
import { Target, X, Filter, Search } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import { useWellFinderStore } from "../../stores/useWellFinderStore";
import { useOperatingAoiContext } from "../../stores/useOperatingAoiContext";
import WellFinderReportButton from './WellFinderReportButton.jsx';
import WellCard from "./WellCard";
import Well3DPanel from '../well3d/Well3DPanel';
import { focusWell } from "./cameraFocus";

const STATUS_CONFIG = {
  strong: { label: "Strong", color: "text-green-400", bg: "bg-green-400/10", border: "border-green-400/20", dot: "bg-green-400" },
  candidate: { label: "Candidate", color: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-400/20", dot: "bg-amber-400" },
  unverified: { label: "Unverified", color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/20", dot: "bg-red-400" },
};

const TABS = [
  { id: "core", label: "Core" },
  { id: "pro", label: "Pro" },
  { id: "regulatory", label: "Regulatory" },
];

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
        <Target size={18} className="text-gray-500" />
      </div>
      <div className="text-xs text-gray-500 leading-relaxed">
        Click a detected well or RRC well on the map to view details and correlation data.
      </div>
      <div className="text-[10px] text-gray-600 leading-relaxed">
        Zoom in past level 11 to see wells.
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="p-4 space-y-3 animate-pulse">
      <div className="h-4 bg-slate-700 rounded w-2/3" />
      <div className="h-3 bg-slate-700 rounded w-1/3" />
      <div className="h-px bg-slate-800 my-3" />
      <div className="h-3 bg-slate-700 rounded w-full" />
      <div className="h-3 bg-slate-700 rounded w-4/5" />
      <div className="h-3 bg-slate-700 rounded w-3/5" />
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.unverified;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.bg} ${cfg.border} ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function WellListItem({ well, selected, onClick }) {
  const cfg = STATUS_CONFIG[well.match_status] ?? STATUS_CONFIG.unverified;
  return (
    <div
      onClick={onClick}
      className={`px-3 py-2.5 border-b border-slate-800/60 cursor-pointer transition-colors ${selected ? "border-l-2 border-l-accent-action" : "hover:bg-slate-800/40"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium text-gray-200 truncate">
            {well.rrc_name || well.detection_id}
          </div>
          {well.matched_api && (
            <div className="text-[10px] text-gray-500 font-mono mt-0.5">API {well.matched_api}</div>
          )}
        </div>
        <StatusBadge status={well.match_status} />
      </div>
      <div className="flex items-center gap-3 mt-1.5">
        <div className="flex items-center gap-1.5 flex-1">
          <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.round(well.confidence * 100)}%`, backgroundColor: cfg.dot.replace("bg-", "") }} />
          </div>
          <span className="text-[10px] text-gray-500 shrink-0">{Math.round(well.confidence * 100)}%</span>
        </div>
        {well.offset_ft != null && (
          <span className="text-[10px] text-gray-500 shrink-0">{well.offset_ft} ft {well.offset_bearing}</span>
        )}
      </div>
    </div>
  );
}

function StatsBar({ stats }) {
  return (
    <div className="grid grid-cols-3 gap-1 px-3 py-2 border-b border-slate-800">
      {["strong", "candidate", "unverified"].map(s => {
        const cfg = STATUS_CONFIG[s];
        return (
          <div key={s} className={`flex flex-col items-center py-1.5 rounded-md border ${cfg.bg} ${cfg.border}`}>
            <span className={`text-[13px] font-semibold ${cfg.color}`}>{stats[s] ?? 0}</span>
            <span className="text-[9px] text-gray-500 font-mono uppercase tracking-wider mt-0.5">{cfg.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function FilterBar({ activeFilters, onToggle, onClear }) {
  const all = ["strong", "candidate", "unverified"];
  const hasFilters = activeFilters.length > 0;
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-800">
      <Filter size={11} className="text-gray-500 shrink-0" />
      <div className="flex items-center gap-1 flex-1 flex-wrap">
        {all.map(s => {
          const cfg = STATUS_CONFIG[s];
          const active = activeFilters.includes(s);
          return (
            <button key={s} onClick={() => onToggle(s)}
              className={`px-2 py-0.5 rounded-full text-[10px] border transition-all ${active ? `${cfg.bg} ${cfg.border} ${cfg.color}` : "bg-transparent border-slate-700 text-gray-500 hover:border-slate-500"}`}>
              {cfg.label}
            </button>
          );
        })}
      </div>
      {hasFilters && (
        <button onClick={onClear} className="text-[10px] text-gray-500 hover:text-gray-300 shrink-0"><X size={11} /></button>
      )}
    </div>
  );
}

function RrcOnlyCard({ onBack }) {
  return (
    <>
      <div className="flex justify-end px-3 pt-2">
        <button onClick={onBack} className="text-[10px] text-gray-500 hover:text-gray-300 flex items-center gap-1">
          <X size={11} /> Back to list
        </button>
      </div>
      <div className="mx-3 mt-3 rounded-lg border border-slate-700/60 bg-slate-800/40 px-4 py-5 flex flex-col items-center gap-2 text-center">
        <div className="w-8 h-8 rounded-full bg-slate-700/60 flex items-center justify-center">
          <Target size={14} className="text-slate-500" />
        </div>
        <p className="text-[11px] text-slate-300 font-medium leading-snug">No detected well match</p>
        <p className="text-[10px] text-slate-500 leading-relaxed">This RRC well has no correlated AIPrime detection. Detail is shown in the right panel.</p>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Well search — filters list, falls back to direct API lookup
// ---------------------------------------------------------------------------

function WellSearch({ value, onChange, displayedWells, mapRef, onDirectResult }) {
  const [lookupStatus, setLookupStatus] = useState(null);
  const timerRef = useRef(null);

  // When search changes, try direct lookup if no list matches after short delay
  useEffect(() => {
    clearTimeout(timerRef.current);
    setLookupStatus(null);

    const digits = value.trim().replace(/\D/g, '');
    if (!digits || digits.length < 5) return;
    if (displayedWells.length > 0) return; // list has matches, no need

    timerRef.current = setTimeout(async () => {
      const api = digits.padStart(8, '0');
      setLookupStatus('searching');
      try {
        const res = await fetchWithAuth(`/api/v1/well-directionals/${api}`);
        const data = await res.json();
        if (!res.ok || data.error) {
          setLookupStatus('notfound');
          return;
        }
        setLookupStatus('found');
        onDirectResult(data);

        const map = mapRef?.current;
        if (map && data.surfLon && data.surfLat) {
          map.flyTo({ center: [data.surfLon, data.surfLat], zoom: 18, duration: 1200 });
        }
      } catch (_) {
        setLookupStatus('error');
      }
    }, 600);

    return () => clearTimeout(timerRef.current);
  }, [value, displayedWells.length]);

  return (
    <div className="px-3 py-2 border-b border-slate-800">
      <div className="relative">
        <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setLookupStatus(null); }}
          placeholder="Filter by API or well name..."
          className="w-full bg-slate-800/60 border border-slate-700 rounded-md pl-7 pr-7 py-1.5 text-[11px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-slate-500"
        />
        {value && (
          <button onClick={() => { onChange(''); setLookupStatus(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400">
            <X size={10} />
          </button>
        )}
      </div>

      {lookupStatus === 'searching' && (
        <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-pulse" />
          Looking up well...
        </div>
      )}
      {lookupStatus === 'notfound' && (
        <div className="text-[10px] text-red-400/70 mt-1">No RRC well found for that API</div>
      )}
      {lookupStatus === 'error' && (
        <div className="text-[10px] text-red-400/70 mt-1">Lookup failed</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export default function WellFinderPanel({ mapRef, mapReady, well3dSurfaceRef }) {
  const [wells, setWells] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [directResult, setDirectResult] = useState(null);

  const selectedWell = useWellFinderStore(s => s.selectedWell);
  const selectedWellId = useWellFinderStore(s => s.selectedWellId);
  const loadingDetail = useWellFinderStore(s => s.loadingDetail);
  const detailError = useWellFinderStore(s => s.detailError);
  const clearSelection = useWellFinderStore(s => s.clearSelection);
  const handleClearSelection = useCallback(() => { clearSelection(); setSearch(''); setDirectResult(null); }, [clearSelection]);
  const stats = useWellFinderStore(s => s.stats);
  const activeFilters = useWellFinderStore(s => s.activeFilters);
  const toggleFilter = useWellFinderStore(s => s.toggleFilter);
  const clearFilters = useWellFinderStore(s => s.clearFilters);
  const selectedRrcOnly = useWellFinderStore(s => s.selectedRrcOnly);
  const activeTab = useWellFinderStore(s => s.activeTab);
  const setActiveTab = useWellFinderStore(s => s.setActiveTab);

  const activeOperatingAoiId = useOperatingAoiContext(s => s.activeOperatingAoiId);

  const fetchWells = useCallback(async () => {
    const map = mapRef?.current;
    if (!map) return;
    const zoom = map.getZoom();
    if (zoom < 11) { setWells([]); return; }
    const b = map.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(",");
    const statusParam = activeFilters.length > 0 ? `&status=${activeFilters[0]}` : "";
    const aoiParam = activeOperatingAoiId ? `&aoi_id=${activeOperatingAoiId}` : "";
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/v1/wells/near?bbox=${bbox}&limit=200${statusParam}${aoiParam}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setWells(data.wells || []);
    } catch (err) {
      console.error("[WellFinderPanel] fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [mapRef, activeFilters, activeOperatingAoiId]);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !mapReady) return;
    fetchWells();
    map.on("moveend", fetchWells);
    return () => map.off("moveend", fetchWells);
  }, [mapReady, fetchWells]);

  const filteredByStatus = activeFilters.length > 0
    ? wells.filter(w => activeFilters.includes(w.match_status))
    : wells;

  const searchTerm = search.trim().toLowerCase().replace(/\D/g, '') || search.trim().toLowerCase();
  const displayedWells = searchTerm
    ? filteredByStatus.filter(w => {
      const api = (w.matched_api || '').toLowerCase();
      const name = (w.rrc_name || w.detection_id || '').toLowerCase();
      const digits = searchTerm.replace(/\D/g, '');
      return api.includes(searchTerm)
        || name.includes(searchTerm)
        || (digits && api.replace(/\D/g, '').includes(digits));
    })
    : filteredByStatus;

  // qc_type 'no_well' is a NEGATIVE detection: the model looked where the
  // registry claims a well and found none. These stay in the payload and on the
  // map on purpose — the finding is part of the product — but they are not
  // wells and were being counted as such in the header. Report both figures
  // rather than silently dropping one.
  const qcClearedCount = displayedWells.filter((w) => w.qc_type === "no_well").length;
  const confirmedCount = displayedWells.length - qcClearedCount;

  return (
    <div className="h-full flex flex-col bg-bg-soft">

      {/* Header */}
      <div className="p-3 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <Target size={12} />
              WellFinder
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {loading
                ? "Loading wells..."
                : `${confirmedCount} wells${qcClearedCount ? ` · ${qcClearedCount} QC-cleared` : ""} in view`}
              {activeOperatingAoiId && <span className="ml-1 text-status-busy">· AOI filtered</span>}
            </div>
          </div>
          {loading && <div className="w-2 h-2 rounded-full bg-status-busy animate-pulse" />}
        </div>
      </div>

      {/* Search + Report */}
      <div className="flex items-stretch gap-2 shrink-0">
        <div className="flex-1 min-w-0">
          <WellSearch
            value={search}
            onChange={v => { setSearch(v); setDirectResult(null); }}
            displayedWells={displayedWells}
            mapRef={mapRef}
            onDirectResult={setDirectResult}
          />
        </div>
        <div className="pr-3 py-2 border-b border-slate-800 flex items-center">
          <WellFinderReportButton mapRef={mapRef} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 shrink-0">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={["flex-1 py-2 text-[11px] uppercase tracking-wider font-medium transition-colors relative",
              activeTab === tab.id ? "text-accent-action" : "text-gray-500 hover:text-gray-300"].join(" ")}>
            {tab.label}
            {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-accent-action" />}
          </button>
        ))}
      </div>

      {/* Tab: Core */}
      {activeTab === "core" && (
        <>
          <StatsBar stats={stats} />
          <FilterBar activeFilters={activeFilters} onToggle={toggleFilter} onClear={clearFilters} />

          <div className="flex-1 overflow-y-auto">
            {!selectedWellId && !loadingDetail && (
              <>
                {displayedWells.length === 0 && !loading && !directResult && <EmptyState />}
                {displayedWells.length === 0 && directResult && (
                  <div
                    className="px-3 py-3 border-b border-slate-800/60 cursor-pointer hover:bg-slate-800/40 transition-colors"
                    onClick={() => {
                      const map = mapRef?.current;
                      if (map && directResult.surfLon) {
                        map.flyTo({ center: [directResult.surfLon, directResult.surfLat], zoom: 18, duration: 800 });
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[11px] font-medium text-gray-200">
                          API {directResult.api}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono mt-0.5">
                          RRC well · {directResult.wellType}
                          {directResult.formation ? ` · ${directResult.formation}` : ''}
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-gray-500 shrink-0">
                        {directResult.tvdFt ? (directResult.tvdFt / 1000).toFixed(1) + 'k ft' : ''}
                      </span>
                    </div>
                    <div className="text-[9px] text-gray-600 mt-1">
                      Click to zoom · no detection match
                    </div>
                  </div>
                )}
                {displayedWells.map(w => (
                  <WellListItem
                    key={w.id}
                    well={w}
                    selected={w.detection_id === selectedWellId}
                    onClick={() => {
                      const map = mapRef?.current;
                      // Select immediately (no detail yet) so the row highlights
                      // right away. The camera move is DEFERRED to the post-fetch
                      // branch: the list row carries no faithful RRC-link flag
                      // (only matched_api, which drops out for deleted RRC assets),
                      // so we gate the left-shift on the real detail.link — parity
                      // with the map-click path, at the cost of one round-trip.
                      useWellFinderStore.getState().selectWell(w.detection_id, "detected", null);
                      fetchWithAuth(`/api/v1/wells/${w.detection_id}/detail`)
                        .then(r => r.ok ? r.json() : null)
                        .then(detail => {
                          if (!detail) return;
                          useWellFinderStore.getState().selectWell(w.detection_id, "detected", detail);
                          const coords = detail.detected
                            ? [detail.detected.lng, detail.detected.lat]
                            : detail.rrc
                              ? [detail.rrc.lng, detail.rrc.lat]
                              : null;
                          focusWell(map, coords, { panelWillOpen: !!detail.link?.rrc_asset_id, duration: 600 });
                        })
                        .catch(console.error);
                    }}
                  />
                ))}
              </>
            )}
            {loadingDetail && !selectedRrcOnly && <Skeleton />}
            {detailError && !loadingDetail && (
              <div className="p-4 text-xs text-red-400">Failed to load well detail: {detailError}</div>
            )}
            {selectedRrcOnly && !loadingDetail && <RrcOnlyCard onBack={clearSelection} />}
            {selectedWell && !loadingDetail && !selectedRrcOnly && (
              <>
                <div className="flex justify-end px-3 pt-2">
                  <button onClick={handleClearSelection} className="text-[10px] text-gray-500 hover:text-gray-300 flex items-center gap-1">
                    <X size={11} /> Back to list
                  </button>
                </div>
                <WellCard detail={selectedWell} />
              </>
            )}
          </div>

          {!selectedWellId && wells.length === 0 && !loading && (
            <div className="px-3 py-2 border-t border-slate-800 text-[10px] text-gray-500 text-center">
              Zoom in past level 11 to see wells
            </div>
          )}
        </>
      )}

      {activeTab === "pro" && (
        <div className="flex-1 min-h-0">
          <Well3DPanel surfaceRef={well3dSurfaceRef} />
        </div>
      )}

      {activeTab === "regulatory" && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-6">
          <div className="w-8 h-8 rounded-full border border-slate-700 flex items-center justify-center mb-1">
            <Target size={14} className="text-gray-600" />
          </div>
          <div className="text-xs text-gray-400 font-mono uppercase tracking-wider">Regulatory</div>
          <div className="text-[10px] text-gray-600 leading-relaxed">Compliance and regulatory data coming soon.</div>
        </div>
      )}

    </div>
  );
}
